import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Divider,
  Checkbox,
  FormControl,
  FormControlLabel,
  Pagination,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as LeftArrowIcon } from "../../../images/leftArrowIcon.svg";
import { ReactComponent as ResetIcon } from "../../../images/resetIcon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { MessagePopupContext } from "../../main-pages/ServicePage";

function UserStoriesUpload({ uploadedUserStories }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [selectedUserStoriesIndexes, setSelectedUserStoriesIndexes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [userStoriesData, setUserStoriesData] = useState([]);
  const [uploadKey, setUploadKey] = useState(0);
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);

  const userStoriesTitles = uploadedUserStories.map((userStory) => userStory["Title"]);
  const itemsPerPage = 7;

  const handleUserStoriesChange = (event, index) => {
    const selectedIndex = index;
    const updatedIndexes = selectedUserStoriesIndexes.includes(selectedIndex)
      ? selectedUserStoriesIndexes.filter((i) => i !== selectedIndex)
      : [...selectedUserStoriesIndexes, selectedIndex];

    setSelectedUserStoriesIndexes(updatedIndexes);
    const selectedStoriesData = uploadedUserStories.filter((_, idx) =>
      updatedIndexes.includes(idx)
    );
    setUserStoriesData(selectedStoriesData);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const openUserStoriesImportedPage = () => {
    if (location.pathname === "/dashboard/userstory/input") {
      navigate("/dashboard/multi-userstory/output", {
        state: { userStories: userStoriesData },
      });
    } else if (location.pathname === "/dashboard/generate-testcases/input") {
      navigate("/dashboard/generate-multi/testcases/output", {
        state: { userStories: userStoriesData },
      });
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUserStories = userStoriesTitles.slice(startIndex, endIndex);

  const handleSelectAll = () => {
    const allIndexes = uploadedUserStories.map((_, index) => index);
    setSelectedUserStoriesIndexes(allIndexes);
    setUserStoriesData(uploadedUserStories);
  };

  const handleDeselectAll = () => {
    setUserStoriesData([]);
    setSelectedUserStoriesIndexes([]);
  };

  const currentItems = uploadedUserStories.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: "500px",
          maxHeight: "90vh", // Use max-height instead of fixed height
          padding: "20px 20px 0px 20px", // Remove bottom padding
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      key={uploadKey}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          flexShrink: 0, // Prevent shrinking
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={handleClose} sx={{ marginRight: "10px" }}>
            <LeftArrowIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Roboto, sans-serif",
              marginLeft: "-14px",
            }}
          >
            Import User Stories
          </Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Subtitle */}
      <Typography
        sx={{
          fontSize: "14px",
          fontFamily: "Roboto, sans-serif",
          paddingLeft: "25px",
          opacity: 0.6,
          marginBottom: "10px",
          flexShrink: 0,
        }}
      >
        Select one or multiple user stories to import
      </Typography>

      {/* Select/Deselect Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          paddingY: 1,
          ml: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={handleSelectAll}
            sx={{
              textTransform: "none",
              minWidth: "50px",
              paddingY: 1,
              color: "#49A3FF",
              '&:hover': {
                backgroundColor: 'rgba(73, 163, 255, 0.1)',
              },
            }}
            // startIcon={
            //   selectedUserStoriesIndexes.length === uploadedUserStories.length ? (
            //     <CheckBoxIcon sx={{ color: "#49A3FF" }} />
            //   ) : (
            //     <CheckBoxOutlineBlankIcon sx={{ color: "#49A3FF" }} />
            //   )
            // }
          >
            Select all
          </Button>

          <Button
            onClick={handleDeselectAll}
            sx={{
              textTransform: "none",
              minWidth: "50px",
              paddingY: 1,
              color: "#49A3FF",
              '&:hover': {
                backgroundColor: 'rgba(73, 163, 255, 0.1)',
              },
            }}
            // startIcon={
            //   selectedUserStoriesIndexes.length === 0 ? (
            //     <CheckBoxOutlineBlankIcon sx={{ color: "#49A3FF" }} />
            //   ) : (
            //     <IndeterminateCheckBoxIcon sx={{ color: "#49A3FF" }} />
            //   )
            // }
            // disabled={selectedUserStoriesIndexes.length === 0}
          >
            Deselect all
          </Button>
        </Box>

        {/* <Typography variant="body2" sx={{ color: "grey" }}>
          <span style={{ color: "#49A3FF" }}>
            {selectedUserStoriesIndexes.length}/
          </span>
          <span style={{ color: "grey" }}>{uploadedUserStories.length}</span> Selected
        </Typography> */}
      </Box>


      {/* User Stories List - Scrollable */}
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "260px",
          backgroundColor: "#F0F8FF",
          border: "1px dashed #49A3FF",
          borderRadius: "7px",
          padding: "10px 30px 0px 50px",
          pl: 3,
          ml: 1,
          marginBottom: "16px", // Add margin bottom
          flexShrink: 0,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
          },
        }}
      >
        <FormControl component="fieldset">
          {paginatedUserStories.map((userstories, index) => (
            <FormControlLabel
              key={startIndex + index}
              control={
                <Checkbox
                  checked={selectedUserStoriesIndexes.includes(startIndex + index)}
                  onChange={(e) => handleUserStoriesChange(e, startIndex + index)}
                  sx={{
                    color: "#49A3FF",
                    '&.Mui-checked': {
                      color: "#49A3FF",
                    },
                  }}
                />
              }
              label={userstories}
              sx={{
                fontSize: "14px",
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                },
              }}
            />
          ))}
        </FormControl>
      </Box>

      {/* Pagination Section */}
      <Box
        sx={{
          display: "flex",
          position: "relative",
          alignItems: "center",
          px: 2,
          justifyContent: "center",
          marginBottom: "8px", // Reduced margin
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flexShrink: 0,
            pl: 3,
            position: "absolute",
            left: 0,
          }}
        >
          {currentItems.length}/{uploadedUserStories.length} Items
        </Typography>
        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={Math.ceil(userStoriesTitles.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          />
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ flexShrink: 0, marginBottom: "8px" }} />

      {/* Dialog Actions - Import Button */}
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          padding: "8px 24px 20px 24px", // Reduced top padding
          flexShrink: 0,
        }}
      >
        <Button
          onClick={openUserStoriesImportedPage}
          variant="contained"
          component="label"
          backgroundColor="#49A3FF"
          disabled={selectedUserStoriesIndexes.length === 0}
          sx={{
            backgroundColor: "#49A3FF",
            textTransform: "none",
          }}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserStoriesUpload;